import { useState, useEffect } from 'react';

const cachedScripts = [];

/** Asynchronously load a script on demand inside a functional component.
 *
 * Lightly adapted from https://usehooks.com/useScript/
 */
export const useScript = (src: string, ready = true) => {
  const [state, setState] = useState({
    loaded: false,
    error: false
  });

  useEffect(
    () => {
      if (!ready) {
        return;
      }

      if (cachedScripts.includes(src)) {
        setState({
          loaded: true,
          error: false
        });
      }
      else {
        cachedScripts.push(src);

        const script = document.createElement('script');
        script.src = src;
        script.async = true;

        const onScriptLoad = () => {
          setState({
            loaded: true,
            error: false
          });
        };

        const onScriptError = () => {
          const index = cachedScripts.indexOf(src);
          if (index >= 0) cachedScripts.splice(index, 1);
          script.remove();
          setState({
            loaded: true,
            error: true
          });
        };

        script.addEventListener('load', onScriptLoad);
        script.addEventListener('error', onScriptError);

        document.body.appendChild(script);

        return () => {
          script.removeEventListener('load', onScriptLoad);
          script.removeEventListener('error', onScriptError);
        };
      }
    },
    [src, ready]
  );

  return [state.loaded, state.error];
};
